"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import type { Product, ColorOption } from "@/types";
import { useCart } from "@/context/CartContext";
import { TagIcon, BagIcon } from "@/components/Icons";
import { colorPhoto, colorPhotos } from "@/lib/product-images";
import { getVariantPricing } from "@/lib/pricing";
import { focalPointStyle } from "@/lib/image-focal-point";
import { COLOR_SWATCH_HEX } from "@/lib/colors";
import { PRODUCT_KAART_SIZES, productFotoSrcSet } from "@/lib/product-image-variants";

interface ProductCardProps {
  product: Product;
  colorOption?: ColorOption; // toon dit als kaart voor één specifieke kleur
  // Koppniveau van de productnaam. 3 = kaart onder een sectiekop (h2), zoals op
  // de homepage; 2 = kaart direct onder de h1 van een overzicht (winkel,
  // categorie), anders slaat de kopstructuur een niveau over (HTML-04).
  headingLevel?: 2 | 3;
}

function formatPrice(price: number) {
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(price);
}

// Eerste foto die bij een kleur hoort — gedeelde logica met de productpagina,
// de winkelwagen en het upsell-blok.
function firstImageForColor(product: Product, opt: ColorOption): string {
  return colorPhoto(product, opt);
}

export default function ProductCard({ product, colorOption, headingLevel = 3 }: ProductCardProps) {
  const Kop = headingLevel === 2 ? "h2" : "h3";
  const [imgError, setImgError] = useState(false);
  // Foto waarvan de kleine variant niet laadde: die toont dan het origineel (zie productFotoSrcSet).
  const [variantMislukt, setVariantMislukt] = useState<string | null>(null);
  const fotoRef = useRef<HTMLImageElement>(null);
  const [idx, setIdx] = useState(0);
  const [hoverImg, setHoverImg] = useState<string | null>(null); // voorbeeld bij hover over een kleurbolletje
  const touchStartX = useRef<number | null>(null);
  const swipedRef = useRef(false);

  // Kleurbolletjes: alle kleurvarianten van dit product (alleen tonen bij >1 kleur)
  const swatches = (product.colorOptions || []).filter((o) => o.name);
  // Compacte weergave: max ~2 rijen tonen, daarna een "+N" naar de productpagina.
  const MAX_SWATCHES = 10;
  const visibleSwatches = swatches.slice(0, MAX_SWATCHES);
  const hiddenSwatchCount = Math.max(0, swatches.length - MAX_SWATCHES);

  // Prijs van de getoonde kleurvariant (bijv. een 6-pack heeft een eigen prijs
  // die kan afwijken van de basisprijs van het product) — anders de productprijs.
  const representativeVariant = colorOption?.variants?.[0];
  const { price: displayPrice, originalPrice, hasSale: isOnSale } = getVariantPricing(product, representativeVariant);
  // "nieuw": hele product, of specifiek de kleurvariant die deze kaart toont.
  // Een product-brede vlag geldt voor elke kleurkaart; een kleur kan ook los
  // als "nieuw" gemarkeerd zijn zonder dat de rest van het product dat is.
  const isNew = !!product.new || (colorOption ? !!colorOption.isNew : (product.colorOptions || []).some((o) => o.isNew));
  const isBestseller = !!product.bestseller;
  const isFeatured = !!product.featured;
  // Eén statuslabel tegelijk linksboven, anders stapelen sale/nieuw/bestseller/
  // uitgelicht zich op tot een muur van rondjes. Prioriteit: sale wint altijd
  // (actie-urgentie), daarna nieuw, dan bestseller, dan uitgelicht.
  const statusBadge = isOnSale ? "sale" : isNew ? "nieuw" : isBestseller ? "bestseller" : isFeatured ? "uitgelicht" : null;

  // Actielabel (bijv. "2+1 gratis") uit de lopende acties voor dit product.
  const { promoLabel } = useCart();
  const promo = promoLabel({ brand: product.brand, slug: product.slug, id: product.id });
  // Op de kaart alleen de kern van het actielabel ("3+1 gratis"); een toelichting
  // tussen haakjes maakt de badge drie regels hoog en overstemt de productfoto.
  // De volledige tekst staat op de productpagina.
  const promoKort = promo ? promo.split("(")[0].trim() : null;

  // Bij een kleuroptie: eigen foto/naam/link (crawlbare pad-URL), anders het hoofdproduct
  const href = colorOption ? `/product/${product.slug}/${colorOption.slug}` : `/product/${product.slug}`;
  const title = colorOption ? `${product.name} - ${colorOption.name}` : product.name;

  // Foto's voor deze kaart. Bij een kleurvariant: alleen de aan déze kleur gekoppelde
  // foto's + de algemene (niet-gekoppelde) foto's — nooit die van een andere kleur.
  const imgList = React.useMemo(() => {
    const lijst = colorPhotos(product, colorOption);
    // Laatste redmiddel als er niets gekoppeld/algemeen is: toon de hoofdfoto
    return lijst.length ? lijst : (product.thumbnail ? [product.thumbnail] : []);
  }, [colorOption, product]);
  const safeIdx = Math.min(idx, Math.max(0, imgList.length - 1));
  const primaryImg = hoverImg || imgList[safeIdx] || imgList[0] || product.thumbnail;
  const canSwipe = imgList.length > 1;

  // Productfoto's hebben kleine webp-varianten in Storage (lib/product-image-variants.ts). In productie
  // bestaat /_next/image niet, dus next/image gaf hier de volledige originele foto zonder srcset; deze
  // gewone <img> laat de browser de kleinste passende variant kiezen. Geen variant bekend (andere bron)
  // of de variant laadt niet: gewoon de originele src.
  const srcSet = primaryImg && variantMislukt !== primaryImg ? productFotoSrcSet(primaryImg) : "";
  function onFotoFout() {
    if (srcSet) setVariantMislukt(primaryImg ?? null);
    else setImgError(true);
  }
  // Een laadfout vóór hydratatie (de foto start in de HTML van de server) haalt onError niet meer in.
  useEffect(() => {
    const el = fotoRef.current;
    if (el && srcSet && el.complete && el.naturalWidth === 0 && el.currentSrc) setVariantMislukt(primaryImg ?? null);
  }, [primaryImg, srcSet]);

  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
    swipedRef.current = false;
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null || !canSwipe) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      swipedRef.current = true; // onderdruk tap-navigatie na swipe
      setIdx((i) => Math.max(0, Math.min(imgList.length - 1, dx < 0 ? i + 1 : i - 1)));
    }
    touchStartX.current = null;
  }

  const hasVariants = (product.sizes?.length || 0) > 0 || (product.colorOptions?.length || 0) > 1 || (product.colors?.length || 0) > 1;

  return (
    <div className="product-card">
      {/* Afbeelding */}
      <Link
        href={href}
        style={{ display: "block", textDecoration: "none" }}
        onClick={(e) => { if (swipedRef.current) { e.preventDefault(); swipedRef.current = false; } }}
      >
        <div 
          className="product-card-image" 
          onTouchStart={onTouchStart} 
          onTouchEnd={onTouchEnd}
          onMouseLeave={() => setIdx(0)}
          style={{ position: "relative" }}
        >
          {!imgError && primaryImg ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element -- next/image optimaliseert in productie niet (geen /_next/image); srcset komt uit eigen varianten */}
              <img
                ref={fotoRef}
                src={primaryImg}
                srcSet={srcSet || undefined}
                sizes={srcSet ? PRODUCT_KAART_SIZES : undefined}
                alt={title}
                loading="lazy"
                decoding="async"
                onError={onFotoFout}
                className="pc-img-primary"
                style={{
                  position: "absolute", inset: 0, width: "100%", height: "100%", color: "transparent",
                  objectFit: "cover", ...focalPointStyle(primaryImg, product.imageFocalPoints),
                }}
              />
              {/* Hoverbook zones */}
              {imgList.length > 1 && (
                <div style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  zIndex: 3
                }}>
                  {imgList.map((_, i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        height: "100%",
                        cursor: "pointer"
                      }}
                      onMouseEnter={() => setIdx(i)}
                    />
                  ))}
                </div>
              )}
              {canSwipe && (
                <span className="pc-dots" aria-hidden="true" style={{ zIndex: 4 }}>
                  {imgList.map((_, i) => (
                    <span key={i} className={`pc-dot${i === safeIdx ? " active" : ""}`} />
                  ))}
                </span>
              )}
            </>
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--color-gray-100)",
                fontSize: "2.5rem",
                color: "var(--color-gray-300)",
              }}
            >
              <BagIcon size="2.25rem" strokeWidth={1.2} />
            </div>
          )}

          {/* Badges: statuslabel (sale/nieuw/bestseller/uitgelicht) en actielabel
              gestapeld linksboven. "nieuw" geldt voor het product óf specifiek
              voor de getoonde kleurvariant. */}
          {(statusBadge || promoKort) && (
            <div className="product-card-badges">
              {statusBadge && (
                <span className={`product-card-badge${statusBadge !== "sale" ? ` badge-${statusBadge}` : ""}`}>
                  {statusBadge}
                </span>
              )}
              {promoKort && (
                <span className="product-card-badge-promo" title={promo || undefined}>
                  <TagIcon size="0.72rem" strokeWidth={2} />
                  <span className="pc-promo-text">{promoKort}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </Link>

      {/* Product info */}
      <div className="product-card-body">
        <Link href={href} style={{ textDecoration: "none" }}>
          <Kop className="product-card-name">
            {title}
          </Kop>
        </Link>

        {/* Prijs */}
        <div className="product-card-price">
          {isOnSale && originalPrice != null && (
            <span className="price-sale">{formatPrice(originalPrice)}</span>
          )}
          <span style={{ color: "var(--color-price)", fontWeight: 500 }}>
            {formatPrice(displayPrice)}
          </span>
          <span className="price-incl">incl btw</span>
        </div>

        {/* Kleurbolletjes: hover toont die kleur, klik gaat naar die variant */}
        {swatches.length > 1 && (
          <div
            onMouseLeave={() => setHoverImg(null)}
            style={{ display: "flex", flexWrap: "wrap", gap: "6px", margin: "2px 0 8px", maxHeight: "38px", overflow: "hidden", alignItems: "center" }}
          >
            {visibleSwatches.map((opt) => {
              const isCurrent = colorOption && opt.slug === colorOption.slug;
              const bg = COLOR_SWATCH_HEX[opt.mainColor] || "#cccccc";
              return (
                <Link
                  key={opt.slug}
                  href={`/product/${product.slug}/${opt.slug}`}
                  aria-label={opt.name}
                  title={opt.name}
                  onMouseEnter={() => setHoverImg(firstImageForColor(product, opt) || null)}
                  style={{
                    width: "16px", height: "16px", borderRadius: "50%",
                    background: bg,
                    border: opt.mainColor === "Wit" ? "1px solid #ccc" : "1px solid rgba(0,0,0,0.15)",
                    boxShadow: isCurrent ? "0 0 0 2px var(--color-primary)" : "none",
                    display: "inline-block", flexShrink: 0, cursor: "pointer",
                  }}
                />
              );
            })}
            {hiddenSwatchCount > 0 && (
              <Link
                href={`/product/${product.slug}`}
                aria-label={`Nog ${hiddenSwatchCount} kleuren bekijken`}
                title={`+${hiddenSwatchCount} meer`}
                style={{
                  fontSize: "0.7rem", fontWeight: 600, color: "var(--color-gray-500)",
                  textDecoration: "none", lineHeight: "16px", flexShrink: 0,
                }}
              >
                +{hiddenSwatchCount}
              </Link>
            )}
          </div>
        )}

        {/* "Opties selecteren" of "Toevoegen aan winkelwagen" */}
        <Link
          href={`/product/${product.slug}`}
          className="btn btn-select"
          style={{ display: "inline-flex", marginTop: "4px" }}
        >
          {hasVariants ? "Opties selecteren" : "Bekijk product"}
        </Link>
      </div>
    </div>
  );
}
